import React, { useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button,
  CircularProgress, Alert, InputAdornment, IconButton, Link
} from '@mui/material';
import {
  Email, Lock, ArrowBack, CheckCircle, Visibility, VisibilityOff
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import careoffLogo from '../../assets/careoff-logo-2.svg';
import { forgotAdminPassword, verifyOtpForAdminPassword, resetAdminPassword } from '../../services/Api';

// ─────────────────────────────────────────────────────────────────────────────
// Steps:
//   1 → Enter Email   → click "Send OTP"
//   2 → Enter OTP     → click "Verify OTP"
//   3 → New Password  → click "Reset Password"
//   4 → Success screen
// ─────────────────────────────────────────────────────────────────────────────

const STEPS = { EMAIL: 1, OTP: 2, NEW_PASSWORD: 3, SUCCESS: 4 };

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(STEPS.EMAIL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // ── Step 1: Send OTP to email ─────────────────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email address'); return; }

    setLoading(true);
    setError(null);

    try {
      const response = await forgotAdminPassword({ email });
      if (response.success || response.data || response.message) {
        setSuccessMsg(`OTP sent to ${email}`);
        setStep(STEPS.OTP);
      } else {
        setError(response.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Verify OTP ────────────────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 4) { setError('Please enter the OTP sent to your email'); return; }

    setLoading(true);
    setError(null);

    try {
      const response = await verifyOtpForAdminPassword({ email, otp });
      if (response.success || response.data || response.message) {
        setSuccessMsg(null);
        setStep(STEPS.NEW_PASSWORD);
      } else {
        setError(response.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: Reset Password ────────────────────────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters'); return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match'); return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await resetAdminPassword({ email, otp, newPassword });
      if (response.success || response.data || response.message) {
        setStep(STEPS.SUCCESS);
      } else {
        setError(response.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP (reuses forgotAdminPassword endpoint) ─────────────────────
  const handleResendOtp = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await forgotAdminPassword({ email });
      if (response.success || response.data || response.message) {
        setSuccessMsg('OTP resent successfully!');
      } else {
        setError(response.message || 'Failed to resend OTP.');
      }
    } catch (err) {
      setError('Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // UI Helpers
  // ─────────────────────────────────────────────────────────────────────────
  const stepMeta = {
    [STEPS.EMAIL]: {
      title: 'Forgot Password?',
      subtitle: 'Enter your registered email address. We\'ll send you a one-time password.',
    },
    [STEPS.OTP]: {
      title: 'Enter OTP',
      subtitle: `We've sent a verification code to ${email}. Please check your inbox.`,
    },
    [STEPS.NEW_PASSWORD]: {
      title: 'Set New Password',
      subtitle: 'Choose a strong new password for your account.',
    },
    [STEPS.SUCCESS]: {
      title: 'Password Reset!',
      subtitle: 'Your password has been updated successfully.',
    },
  };

  const { title, subtitle } = stepMeta[step];

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        p: 2,
        overflow: 'hidden',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 4, md: 6 },
          width: '100%',
          maxWidth: 450,
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }}
      >
        {/* Logo */}
        <Box
          component="img"
          src={careoffLogo}
          alt="CareOff"
          sx={{ height: 48, mb: 2 }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />

        {/* Step indicator dots */}
        {step !== STEPS.SUCCESS && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {[STEPS.EMAIL, STEPS.OTP, STEPS.NEW_PASSWORD].map((s) => (
              <Box
                key={s}
                sx={{
                  width: step === s ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: step >= s ? 'primary.main' : 'rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Box>
        )}

        <Typography variant="h5" fontWeight={700} gutterBottom textAlign="center">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          {subtitle}
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMsg && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }} onClose={() => setSuccessMsg(null)}>
            {successMsg}
          </Alert>
        )}

        {/* ── STEP 1: Email ───────────────────────────────────────────────── */}
        {step === STEPS.EMAIL && (
          <Box component="form" onSubmit={handleSendOtp} sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your registered email"
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ py: 1.5, fontWeight: 600, borderRadius: '8px', textTransform: 'none', mb: 2 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Send OTP'}
            </Button>
            <Box textAlign="center">
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate('/login')}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                <ArrowBack fontSize="small" /> Back to Login
              </Link>
            </Box>
          </Box>
        )}

        {/* ── STEP 2: OTP ─────────────────────────────────────────────────── */}
        {step === STEPS.OTP && (
          <Box component="form" onSubmit={handleVerifyOtp} sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label="One-Time Password (OTP)"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter the OTP"
              margin="normal"
              inputProps={{ maxLength: 6, inputMode: 'numeric', style: { letterSpacing: '0.35em', fontSize: '1.2rem', textAlign: 'center' } }}
              sx={{ mb: 1 }}
            />
            <Box textAlign="right" sx={{ mb: 2 }}>
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={handleResendOtp}
                disabled={loading}
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Resend OTP
              </Link>
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ py: 1.5, fontWeight: 600, borderRadius: '8px', textTransform: 'none', mb: 2 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Verify OTP'}
            </Button>
            <Box textAlign="center">
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => { setStep(STEPS.EMAIL); setError(null); setSuccessMsg(null); }}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                <ArrowBack fontSize="small" /> Change Email
              </Link>
            </Box>
          </Box>
        )}

        {/* ── STEP 3: New Password ─────────────────────────────────────────── */}
        {step === STEPS.NEW_PASSWORD && (
          <Box component="form" onSubmit={handleResetPassword} sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label="New Password"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><Lock color="action" /></InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNew(!showNew)} edge="end" size="small">
                      {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              margin="normal"
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><Lock color="action" /></InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end" size="small">
                      {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ py: 1.5, fontWeight: 600, borderRadius: '8px', textTransform: 'none', mb: 2 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Reset Password'}
            </Button>
          </Box>
        )}

        {/* ── STEP 4: Success ──────────────────────────────────────────────── */}
        {step === STEPS.SUCCESS && (
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You can now sign in with your new password.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
              sx={{ py: 1.5, fontWeight: 600, borderRadius: '8px', textTransform: 'none' }}
            >
              Back to Login
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ForgotPassword;
